import React from 'react';
import {
    View,
    Text,
    StyleSheet,
    ScrollView,
    TextInput,
    TouchableOpacity,
    Dimensions,
    ActivityIndicator,
    Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigation } from '@react-navigation/native';

const { width } = Dimensions.get('window');

// Detect if we're in development (Expo dev server) or production
const isDev = typeof window !== 'undefined' && window.location && (window.location.port === '19006' || window.location.port === '8081');
const API_BASE = isDev ? 'http://localhost:4000' : '';

const GSTFilingScreen = () => {
    const { colors } = useTheme();
    const navigation = useNavigation();
    const isMobile = width < 768;
    const isSmallMobile = width < 400;

    const [formData, setFormData] = React.useState({
        name: '',
        email: '',
        phone: '',
        businessType: '',
        gstType: '',
        message: '',
    });
    const [loading, setLoading] = React.useState(false);
    const [submitted, setSubmitted] = React.useState(false);
    const [error, setError] = React.useState('');

    const gstTypes = [
        { id: 'new-registration', label: 'New GST Registration' },
        { id: 'return-filing', label: 'GST Return Filing' },
        { id: 'amendment', label: 'GST Amendment' },
        { id: 'cancellation', label: 'GST Cancellation' },
        { id: 'annual-return', label: 'Annual Return (GSTR-9)' },
    ];

    const features = [
        { icon: 'shield-checkmark', title: 'Compliant Filing', desc: 'All filings as per latest GST rules' },
        { icon: 'time', title: 'Timely Submissions', desc: 'Never miss a deadline again' },
        { icon: 'document-text', title: 'Complete Documentation', desc: 'We handle all paperwork' },
        { icon: 'headset', title: 'Expert Support', desc: 'Dedicated GST professionals' },
    ];

    const handleSubmit = async () => {
        if (!formData.name || !formData.email || !formData.phone) {
            setError('Please fill in all required fields');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await fetch(`${API_BASE}/api/service-quote`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: formData.name,
                    email: formData.email,
                    phone: formData.phone,
                    service: 'gst-filing',
                    businessType: formData.businessType || formData.gstType,
                    message: formData.message,
                }),
            });

            const data = await response.json();

            if (data.ok) {
                setSubmitted(true);
            } else {
                setError(data.error || 'Something went wrong. Please try again.');
            }
        } catch (e) {
            setError('Unable to submit request. Please try again or contact us directly.');
        } finally {
            setLoading(false);
        }
    };

    const styles = StyleSheet.create({
        container: { flex: 1, backgroundColor: colors.background },
        scrollView: { flex: 1 },
        hero: {
            backgroundColor: '#1a1a4e',
            paddingVertical: isMobile ? 40 : 60,
            paddingHorizontal: isSmallMobile ? 12 : 20,
        },
        heroContent: {
            maxWidth: 1100,
            alignSelf: 'center',
            width: '100%',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: 'center',
            gap: 40,
        },
        heroLeft: { flex: 1 },
        heroLabel: {
            fontSize: 13,
            color: '#c9a44e',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: 1,
            marginBottom: 10,
        },
        heroTitle: {
            fontSize: isMobile ? 32 : 44,
            fontWeight: '800',
            color: '#fff',
            marginBottom: 16,
            lineHeight: isMobile ? 40 : 52,
        },
        heroSubtitle: {
            fontSize: 16,
            color: 'rgba(255,255,255,0.8)',
            lineHeight: 26,
            marginBottom: 24,
        },
        heroRight: {
            flex: isMobile ? undefined : 1,
            width: isMobile ? '100%' : undefined,
        },
        formCard: {
            backgroundColor: '#fff',
            borderRadius: isSmallMobile ? 12 : 16,
            padding: isSmallMobile ? 16 : 28,
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 8 },
            shadowOpacity: 0.15,
            shadowRadius: 24,
            elevation: 12,
        },
        formTitle: {
            fontSize: isSmallMobile ? 18 : 22,
            fontWeight: '700',
            color: '#1a1a4e',
            marginBottom: 6,
        },
        formSubtitle: {
            fontSize: isSmallMobile ? 12 : 14,
            color: '#6b7280',
            marginBottom: isSmallMobile ? 12 : 20,
        },
        inputGroup: { marginBottom: 16 },
        label: {
            fontSize: 14,
            fontWeight: '600',
            color: '#374151',
            marginBottom: 8,
        },
        required: { color: '#dc2626' },
        input: {
            backgroundColor: '#f8fafc',
            borderWidth: 1,
            borderColor: '#e5e7eb',
            borderRadius: 8,
            paddingHorizontal: 14,
            paddingVertical: 12,
            fontSize: 15,
            color: '#1f2937',
        },
        typeGrid: {
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: 10,
        },
        typeButton: {
            paddingVertical: 10,
            paddingHorizontal: 14,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: '#e5e7eb',
            backgroundColor: '#f8fafc',
        },
        typeButtonSelected: {
            backgroundColor: '#1a1a4e',
            borderColor: '#1a1a4e',
        },
        typeButtonText: {
            fontSize: 13,
            color: '#374151',
        },
        typeButtonTextSelected: {
            color: '#c9a44e',
        },
        textArea: {
            height: 80,
            textAlignVertical: 'top',
        },
        submitButton: {
            backgroundColor: '#c9a44e',
            paddingVertical: 16,
            borderRadius: 8,
            alignItems: 'center',
            marginTop: 8,
        },
        submitButtonText: {
            color: '#1a1a4e',
            fontWeight: '700',
            fontSize: 16,
        },
        errorText: {
            color: '#dc2626',
            fontSize: 14,
            marginBottom: 12,
            textAlign: 'center',
        },
        successCard: {
            backgroundColor: '#fff',
            borderRadius: 16,
            padding: 40,
            alignItems: 'center',
        },
        successIcon: {
            width: 80,
            height: 80,
            borderRadius: 40,
            backgroundColor: '#10b981',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: 20,
        },
        successTitle: {
            fontSize: 24,
            fontWeight: '700',
            color: '#1a1a4e',
            marginBottom: 12,
            textAlign: 'center',
        },
        successText: {
            fontSize: 15,
            color: '#6b7280',
            textAlign: 'center',
            lineHeight: 24,
        },
        featuresSection: {
            paddingVertical: 60,
            paddingHorizontal: 20,
            backgroundColor: '#f8fafc',
        },
        featuresContainer: {
            maxWidth: 1100,
            alignSelf: 'center',
            width: '100%',
        },
        sectionTitle: {
            fontSize: isMobile ? 28 : 36,
            fontWeight: '800',
            color: '#1a1a4e',
            textAlign: 'center',
            marginBottom: 40,
        },
        featuresGrid: {
            flexDirection: 'row',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 24,
        },
        featureCard: {
            backgroundColor: '#fff',
            borderRadius: 12,
            padding: 24,
            width: isMobile ? '100%' : '22%',
            minWidth: 220,
            alignItems: 'center',
            borderWidth: 1,
            borderColor: '#e5e7eb',
        },
        featureIcon: {
            width: 56,
            height: 56,
            borderRadius: 28,
            backgroundColor: '#1a1a4e',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: 16,
        },
        featureTitle: {
            fontSize: 16,
            fontWeight: '700',
            color: '#1a1a4e',
            marginBottom: 8,
            textAlign: 'center',
        },
        featureDesc: {
            fontSize: 14,
            color: '#6b7280',
            textAlign: 'center',
        },
        servicesSection: {
            paddingVertical: 60,
            paddingHorizontal: 20,
            backgroundColor: '#fff',
        },
        servicesGrid: {
            flexDirection: 'row',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 16,
            maxWidth: 900,
            alignSelf: 'center',
        },
        serviceItem: {
            flexDirection: 'row',
            alignItems: 'center',
            backgroundColor: '#f8fafc',
            paddingVertical: 14,
            paddingHorizontal: 20,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: '#e5e7eb',
            gap: 12,
        },
        serviceItemText: {
            fontSize: 15,
            color: '#1f2937',
        },
    });

    return (
        <View style={styles.container}>
            <Header />
            <ScrollView style={styles.scrollView}>
                <View style={styles.hero}>
                    <View style={styles.heroContent}>
                        <View style={styles.heroLeft}>
                            <Text style={styles.heroLabel}>Professional GST Services</Text>
                            <Text style={styles.heroTitle}>GST Filing & Registration</Text>
                            <Text style={styles.heroSubtitle}>
                                Complete GST registration and timely returns filing to keep your business compliant and penalty-free. Our experts ensure accurate filings with proper input tax credit claims.
                            </Text>
                        </View>
                        <View style={styles.heroRight}>
                            {!submitted ? (
                                <View style={styles.formCard}>
                                    <Text style={styles.formTitle}>Get a Free Quote</Text>
                                    <Text style={styles.formSubtitle}>Fill the form and our team will connect with you</Text>

                                    {error ? <Text style={styles.errorText}>{error}</Text> : null}

                                    <View style={styles.inputGroup}>
                                        <Text style={styles.label}>Full Name <Text style={styles.required}>*</Text></Text>
                                        <TextInput
                                            style={styles.input}
                                            placeholder="Enter your name"
                                            placeholderTextColor="#9ca3af"
                                            value={formData.name}
                                            onChangeText={(text) => setFormData({ ...formData, name: text })}
                                        />
                                    </View>

                                    <View style={styles.inputGroup}>
                                        <Text style={styles.label}>Email Address <Text style={styles.required}>*</Text></Text>
                                        <TextInput
                                            style={styles.input}
                                            placeholder="Enter your email"
                                            placeholderTextColor="#9ca3af"
                                            keyboardType="email-address"
                                            value={formData.email}
                                            onChangeText={(text) => setFormData({ ...formData, email: text })}
                                        />
                                    </View>

                                    <View style={styles.inputGroup}>
                                        <Text style={styles.label}>Phone Number <Text style={styles.required}>*</Text></Text>
                                        <TextInput
                                            style={styles.input}
                                            placeholder="Enter your phone"
                                            placeholderTextColor="#9ca3af"
                                            keyboardType="phone-pad"
                                            value={formData.phone}
                                            onChangeText={(text) => setFormData({ ...formData, phone: text })}
                                        />
                                    </View>

                                    <View style={styles.inputGroup}>
                                        <Text style={styles.label}>Service Required</Text>
                                        <View style={styles.typeGrid}>
                                            {gstTypes.map((type) => (
                                                <TouchableOpacity
                                                    key={type.id}
                                                    style={[styles.typeButton, formData.gstType === type.id && styles.typeButtonSelected]}
                                                    onPress={() => setFormData({ ...formData, gstType: type.id })}
                                                >
                                                    <Text style={[styles.typeButtonText, formData.gstType === type.id && styles.typeButtonTextSelected]}>
                                                        {type.label}
                                                    </Text>
                                                </TouchableOpacity>
                                            ))}
                                        </View>
                                    </View>

                                    <View style={styles.inputGroup}>
                                        <Text style={styles.label}>Additional Details</Text>
                                        <TextInput
                                            style={[styles.input, styles.textArea]}
                                            placeholder="Tell us about your requirements..."
                                            placeholderTextColor="#9ca3af"
                                            multiline
                                            value={formData.message}
                                            onChangeText={(text) => setFormData({ ...formData, message: text })}
                                        />
                                    </View>

                                    <TouchableOpacity
                                        style={styles.submitButton}
                                        onPress={handleSubmit}
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <ActivityIndicator color="#1a1a4e" />
                                        ) : (
                                            <Text style={styles.submitButtonText}>Submit Request</Text>
                                        )}
                                    </TouchableOpacity>
                                </View>
                            ) : (
                                <View style={styles.successCard}>
                                    <View style={styles.successIcon}>
                                        <Ionicons name="checkmark" size={40} color="#fff" />
                                    </View>
                                    <Text style={styles.successTitle}>Request Submitted!</Text>
                                    <Text style={styles.successText}>
                                        Thank you for your inquiry. We've sent a confirmation email to {formData.email}. Our team will connect with you within 24-48 hours.
                                    </Text>
                                </View>
                            )}
                        </View>
                    </View>
                </View>

                <View style={styles.featuresSection}>
                    <View style={styles.featuresContainer}>
                        <Text style={styles.sectionTitle}>Why Choose Our GST Services?</Text>
                        <View style={styles.featuresGrid}>
                            {features.map((feature, index) => (
                                <View key={index} style={styles.featureCard}>
                                    <View style={styles.featureIcon}>
                                        <Ionicons name={feature.icon} size={26} color="#c9a44e" />
                                    </View>
                                    <Text style={styles.featureTitle}>{feature.title}</Text>
                                    <Text style={styles.featureDesc}>{feature.desc}</Text>
                                </View>
                            ))}
                        </View>
                    </View>
                </View>

                <View style={styles.servicesSection}>
                    <View style={styles.featuresContainer}>
                        <Text style={styles.sectionTitle}>Our GST Services Include</Text>
                        <View style={styles.servicesGrid}>
                            {['New GST Registration', 'GSTR-1 Filing', 'GSTR-3B Filing', 'Annual Return (GSTR-9)', 'GST Amendments', 'GST Cancellation', 'Input Tax Credit', 'GST Compliance Check'].map((service, index) => (
                                <View key={index} style={styles.serviceItem}>
                                    <Ionicons name="checkmark-circle" size={20} color="#10b981" />
                                    <Text style={styles.serviceItemText}>{service}</Text>
                                </View>
                            ))}
                        </View>
                    </View>
                </View>

                {/* GST Process Section */}
                <View style={[styles.servicesSection, { backgroundColor: '#f8fafc' }]}>
                    <View style={styles.featuresContainer}>
                        <Text style={styles.sectionTitle}>How GST Registration Works</Text>
                        <Text style={{ fontSize: 15, color: '#6b7280', textAlign: 'center', marginBottom: 40, maxWidth: 700, alignSelf: 'center' }}>
                            Our streamlined process makes GST registration simple and hassle-free. We handle everything from documentation to final approval.
                        </Text>
                        <View style={{ flexDirection: isMobile ? 'column' : 'row', gap: 24 }}>
                            {[
                                { step: '1', title: 'Submit Documents', desc: 'Provide your PAN, Aadhaar, business proof and bank details' },
                                { step: '2', title: 'Application Filing', desc: 'We prepare and file your GST application on the portal' },
                                { step: '3', title: 'Verification', desc: 'GST department verifies documents and may request clarification' },
                                { step: '4', title: 'GSTIN Issued', desc: 'Receive your 15-digit GSTIN within 7-10 working days' },
                            ].map((item, index) => (
                                <View key={index} style={{ flex: 1, backgroundColor: '#fff', padding: 24, borderRadius: 12, borderWidth: 1, borderColor: '#e5e7eb', alignItems: 'center' }}>
                                    <View style={{ width: 48, height: 48, borderRadius: 24, backgroundColor: '#1a1a4e', justifyContent: 'center', alignItems: 'center', marginBottom: 16 }}>
                                        <Text style={{ color: '#c9a44e', fontSize: 20, fontWeight: '700' }}>{item.step}</Text>
                                    </View>
                                    <Text style={{ fontSize: 16, fontWeight: '700', color: '#1a1a4e', marginBottom: 8, textAlign: 'center' }}>{item.title}</Text>
                                    <Text style={{ fontSize: 14, color: '#6b7280', textAlign: 'center' }}>{item.desc}</Text>
                                </View>
                            ))}
                        </View>
                    </View>
                </View>

                {/* Documents Required Section */}
                <View style={styles.servicesSection}>
                    <View style={styles.featuresContainer}>
                        <Text style={styles.sectionTitle}>Documents Required for GST Registration</Text>
                        <View style={{ flexDirection: isMobile ? 'column' : 'row', gap: 32, maxWidth: 900, alignSelf: 'center' }}>
                            <View style={{ flex: 1 }}>
                                <Text style={{ fontSize: 18, fontWeight: '700', color: '#1a1a4e', marginBottom: 16 }}>For Individuals & Proprietors</Text>
                                {['PAN Card of the proprietor', 'Aadhaar Card', 'Passport size photograph', 'Bank account statement / Cancelled cheque', 'Electricity bill / Rent agreement for address proof'].map((doc, i) => (
                                    <View key={i} style={{ flexDirection: 'row', alignItems: 'flex-start', marginBottom: 12, gap: 10 }}>
                                        <Ionicons name="document-text" size={18} color="#c9a44e" style={{ marginTop: 2 }} />
                                        <Text style={{ fontSize: 15, color: '#374151', flex: 1 }}>{doc}</Text>
                                    </View>
                                ))}
                            </View>
                            <View style={{ flex: 1 }}>
                                <Text style={{ fontSize: 18, fontWeight: '700', color: '#1a1a4e', marginBottom: 16 }}>For Companies & LLPs</Text>
                                {['PAN Card of the company/LLP', 'Certificate of Incorporation', 'MOA / AOA / LLP Agreement', 'PAN & Aadhaar of all directors/partners', 'Board Resolution / Authorization letter', 'Registered office address proof'].map((doc, i) => (
                                    <View key={i} style={{ flexDirection: 'row', alignItems: 'flex-start', marginBottom: 12, gap: 10 }}>
                                        <Ionicons name="document-text" size={18} color="#c9a44e" style={{ marginTop: 2 }} />
                                        <Text style={{ fontSize: 15, color: '#374151', flex: 1 }}>{doc}</Text>
                                    </View>
                                ))}
                            </View>
                        </View>
                    </View>
                </View>

                {/* GST FAQ Section */}
                <View style={[styles.servicesSection, { backgroundColor: '#f8fafc' }]}>
                    <View style={styles.featuresContainer}>
                        <Text style={styles.sectionTitle}>Frequently Asked Questions</Text>
                        {[
                            { q: 'Who needs GST registration?', a: 'Any business with turnover exceeding Rs. 40 lakhs (Rs. 20 lakhs for services) must register for GST. Interstate sellers and e-commerce operators need registration regardless of turnover.' },
                            { q: 'How long does GST registration take?', a: 'GST registration typically takes 7-10 working days if all documents are in order. We ensure your application is complete to avoid delays.' },
                            { q: 'What is the penalty for not registering?', a: 'Operating without GST when required attracts a penalty of 10% of tax due (minimum Rs. 10,000) or 100% for deliberate tax evasion.' },
                            { q: 'Can I file GST returns myself?', a: 'Yes, but professional filing ensures accuracy, proper ITC claims, and avoids costly errors. Our experts handle all compliance requirements.' },
                        ].map((faq, index) => (
                            <View key={index} style={{ backgroundColor: '#fff', padding: 20, borderRadius: 10, marginBottom: 16, borderWidth: 1, borderColor: '#e5e7eb' }}>
                                <Text style={{ fontSize: 16, fontWeight: '700', color: '#1a1a4e', marginBottom: 10 }}>{faq.q}</Text>
                                <Text style={{ fontSize: 15, color: '#6b7280', lineHeight: 24 }}>{faq.a}</Text>
                            </View>
                        ))}
                    </View>
                </View>

                {/* CTA Section */}
                <View style={{ backgroundColor: '#1a1a4e', paddingVertical: 60, paddingHorizontal: 20 }}>
                    <View style={{ maxWidth: 700, alignSelf: 'center', alignItems: 'center' }}>
                        <Text style={{ fontSize: isMobile ? 24 : 32, fontWeight: '800', color: '#c9a44e', textAlign: 'center', marginBottom: 16 }}>
                            Ready to Get GST Compliant?
                        </Text>
                        <Text style={{ fontSize: 16, color: 'rgba(255,255,255,0.8)', textAlign: 'center', marginBottom: 30 }}>
                            Get started with professional GST services today. Our experts are ready to help you with registration, filing, and compliance.
                        </Text>
                        <TouchableOpacity
                            style={{ backgroundColor: '#c9a44e', paddingVertical: 16, paddingHorizontal: 40, borderRadius: 8 }}
                            onPress={() => navigation.navigate('Contact')}
                        >
                            <Text style={{ color: '#1a1a4e', fontWeight: '700', fontSize: 16 }}>Contact Us Now</Text>
                        </TouchableOpacity>
                    </View>
                </View>

                <Footer />
            </ScrollView>
        </View>
    );
};

export default GSTFilingScreen;
